import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInput } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { provideGalaxyDateAdapter } from '@vendasta/galaxy/datepicker';
import { GalaxyUploaderModule } from '@vendasta/galaxy/uploader';
import { SmsDocumentOption, SmsDocumentUploaderComponent } from '../document-uploader/sms-document-uploader.component';

import { MatButton } from '@angular/material/button';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { SmsAddressFieldsComponent } from '../address-fields/sms-address-fields.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Attribute, CreateRegulationInfoRequest, RegulationApiService, SupportingDocumentInfo } from '@vendasta/smsv2';
import { RegulationStatus } from '@vendasta/protos/sms/v2/enums';
import type { RegulationInfo } from '@vendasta/protos/sms/v2/types';
import { SmsContextToken } from '../../../../token';
import { SmsContext } from '../../../../interface';
import { firstValueFrom } from 'rxjs';
import { RegulationService } from '../../regulation.service';
import {
  DOCUMENT_ID_CONTROL,
  DOCUMENT_TYPE_CONTROL,
  PROOF_DOCUMENT_CONTROL,
} from '../document-uploader/sms-document-uploader.component';
import { TranslateModule } from '@ngx-translate/core';
import { SMSI18NModule } from '../../../../assets/i18n/sms-i18n.module';
import { MatRadioModule } from '@angular/material/radio';
import {
  BUSINESS_NAME_FOR_PROCESSING_ADDRESS,
  CITY_FOR_PROCESSING_ADDRESS,
  COUNTRY_FOR_PROCESSING_ADDRESS,
  POSTAL_CODE_FOR_PROCESSING_ADDRESS,
  STATE_FOR_PROCESSING_ADDRESS,
  STREET_ADDRESS_FOR_PROCESSING_ADDRESS,
  STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS,
} from '../sms-regulatory-form.component';
import { formatDateForBackend, parseDateFromBackend } from '../../regulation-helper-functions';

const ITALY_COUNTRY_CODE = 'IT';

const BUSINESS_REGISTRATION_DOCUMENT_TYPE = 'business_registration';

const ADDRESS_DOCUMENT_OPTIONS: SmsDocumentOption[] = [
  {
    label: 'SMS_REGULATION.ITALY.BUSINESS_ADDRESS.ACCEPTED_DOCUMENTS.BUSINESS_REGISTRY',
    value: BUSINESS_REGISTRATION_DOCUMENT_TYPE,
  },
  {
    label: 'SMS_REGULATION.ITALY.BUSINESS_ADDRESS.ACCEPTED_DOCUMENTS.UTILITY_BILL',
    value: 'utility_bill',
  },
  {
    label: 'SMS_REGULATION.ITALY.BUSINESS_ADDRESS.ACCEPTED_DOCUMENTS.TAX_NOTICE',
    value: 'tax_document',
  },
  {
    label: 'SMS_REGULATION.ITALY.BUSINESS_ADDRESS.ACCEPTED_DOCUMENTS.RENT_RECEIPT',
    value: 'rental_receipt',
  },
  {
    label: 'SMS_REGULATION.ITALY.BUSINESS_ADDRESS.ACCEPTED_DOCUMENTS.TITLE_DEED',
    value: 'title_deed',
  },
];

@Component({
  selector: 'sms-italy-form',
  imports: [
    MatCardModule,
    GalaxyFormFieldModule,
    MatInput,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    GalaxyUploaderModule,
    SmsDocumentUploaderComponent,
    MatButton,
    GalaxyAlertModule,
    SmsAddressFieldsComponent,
    ReactiveFormsModule,
    TranslateModule,
    SMSI18NModule,
    MatRadioModule,
  ],
  providers: [provideGalaxyDateAdapter()],
  templateUrl: './sms-italy-form.component.html',
  styleUrl: './sms-italy-form.component.scss',
})
export class SmsItalyFormComponent {
  private readonly snackbarService = inject(SnackbarService);
  private readonly regulationApiService = inject(RegulationApiService);
  private readonly regulationService = inject(RegulationService);
  private readonly context = inject<SmsContext>(SmsContextToken);

  isSoleProprietor = signal(false);

  // Twilio's "Italy: Local - Individual" regulation does not accept business_registration
  // as proof of address
  protected readonly addressDocumentOptions = computed(() =>
    this.isSoleProprietor()
      ? ADDRESS_DOCUMENT_OPTIONS.filter((option) => option.value !== BUSINESS_REGISTRATION_DOCUMENT_TYPE)
      : ADDRESS_DOCUMENT_OPTIONS,
  );

  businessFormGroup = new FormGroup({
    businessName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    vat: new FormControl('', { nonNullable: true, validators: Validators.required }),
  });

  businessAddressFormGroup = new FormGroup({});

  contactFormGroup = new FormGroup({
    contactFirstName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactLastName: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactBirthDate: new FormControl<Date | string>('', { nonNullable: true, validators: Validators.required }),
    contactBirthPlace: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactNationality: new FormControl('', { nonNullable: true, validators: Validators.required }),
    taxIdNumber: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactDocumentNumber: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactIssuingAuthority: new FormControl('', { nonNullable: true, validators: Validators.required }),
    contactIssueDate: new FormControl<Date | string>('', { nonNullable: true, validators: Validators.required }),
  });

  formGroup = new FormGroup({
    business: this.businessFormGroup,
    businessAddress: this.businessAddressFormGroup,
    contact: this.contactFormGroup,
  });

  protected readonly $regulationInfo = toSignal(this.regulationService.regulationInfo$, {
    initialValue: null as RegulationInfo | null,
  });

  protected readonly $regulationStatus = toSignal(this.regulationService.regulationStatus$, {
    initialValue: RegulationStatus.REGULATION_STATUS_UNSPECIFIED,
  });

  protected readonly $shouldFormBeLocked = computed(() => {
    const status = this.$regulationStatus();
    return (
      status === RegulationStatus.REGULATION_STATUS_PENDING_REVIEW ||
      status === RegulationStatus.REGULATION_STATUS_APPROVED
    );
  });

  protected readonly $isRejected = computed(
    () => this.$regulationStatus() === RegulationStatus.REGULATION_STATUS_REJECTED,
  );

  protected readonly $submitting = signal(false);

  constructor() {
    effect(() => {
      this.applySubmissionToForm(this.$regulationInfo());
    });
  }

  private applySubmissionToForm(info: RegulationInfo | null): void {
    if (!info) {
      this.formGroup.enable({ emitEvent: false });
      return;
    }

    const attrs = new Map(info.businessAttributes.map((a) => [a.key, a.value]));
    this.isSoleProprietor.set(attrs.get('is_sole_proprietor') === 'true');
    this.businessFormGroup.patchValue(
      {
        businessName: attrs.get('business_name') ?? '',
        vat: attrs.get('vat') ?? '',
      },
      { emitEvent: false },
    );

    const contactDoc = info.supportingDocuments.find((cd) => !cd.processAddress);
    const contactDocAttrs = new Map((contactDoc?.attributes ?? []).map((a) => [a.key, a.value]));
    this.contactFormGroup.patchValue(
      {
        contactFirstName: attrs.get('first_name') ?? '',
        contactLastName: attrs.get('last_name') ?? '',
        contactBirthDate: parseDateFromBackend(attrs.get('birth_date')),
        contactBirthPlace: attrs.get('birth_place') ?? '',
        contactNationality: attrs.get('nationality') ?? '',
        taxIdNumber: attrs.get('tax_id_number') ?? '',
        contactDocumentNumber: contactDocAttrs.get('document_number') ?? '',
        contactIssuingAuthority: contactDocAttrs.get('issuing_authority') ?? '',
        contactIssueDate: parseDateFromBackend(contactDocAttrs.get('issue_date')),
      },
      { emitEvent: false },
    );

    const addressDoc = info.supportingDocuments.find((sd) => sd.processAddress);
    const addressAttrs = new Map((addressDoc?.attributes ?? []).map((a) => [a.key, a.value]));
    const addressGroup = this.businessAddressFormGroup.get('address') as unknown as FormGroup | null;
    addressGroup?.patchValue(
      {
        streetAddress1: addressAttrs.get(STREET_ADDRESS_FOR_PROCESSING_ADDRESS) ?? '',
        streetAddress2: addressAttrs.get(STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS) ?? '',
        city: addressAttrs.get(CITY_FOR_PROCESSING_ADDRESS) ?? '',
        state: addressAttrs.get(STATE_FOR_PROCESSING_ADDRESS) ?? '',
        postalCode: addressAttrs.get(POSTAL_CODE_FOR_PROCESSING_ADDRESS) ?? '',
      },
      { emitEvent: false },
    );

    if (this.$shouldFormBeLocked()) {
      this.formGroup.disable({ emitEvent: false });
    } else {
      this.formGroup.enable({ emitEvent: false });
    }
  }

  async submitRegulatoryForm() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      this.snackbarService.openErrorSnack('Please fill out all required fields');
      return;
    }
    if (this.$submitting()) return;

    this.$submitting.set(true);
    try {
      const ownerId = await firstValueFrom(this.context.ownerId$);
      const request = this.convertFormToRequest(ownerId);
      await firstValueFrom(this.regulationApiService.createRegulationInfo(request));

      this.regulationService.refresh();
      this.$submitting.set(false);
      this.snackbarService.openSuccessSnack('Regulatory information submitted');
    } catch {
      this.snackbarService.openErrorSnack('Failed to submit regulatory information');
      this.$submitting.set(false);
    }
  }

  private convertFormToRequest(ownerID: string): CreateRegulationInfoRequest {
    let req = new CreateRegulationInfoRequest();
    req.countryCode = ITALY_COUNTRY_CODE;
    req.ownerId = ownerID;
    req.ownerType = this.context.ownerType;
    req.supportingDocuments = [];

    req = this.addAttributesToRequest(req);
    req = this.addContactSupportingDocument(req);
    req = this.addBusinessAddressSupportingDocument(req);

    return req;
  }

  private addAttributesToRequest(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const businessNameAttribute = new Attribute({
      key: 'business_name',
      value: this.businessFormGroup.controls.businessName.value || '',
    });
    const birthDateAttribute = new Attribute({
      key: 'birth_date',
      value: formatDateForBackend(this.contactFormGroup.controls.contactBirthDate.value),
    });
    const birthPlaceAttribute = new Attribute({
      key: 'birth_place',
      value: this.contactFormGroup.controls.contactBirthPlace.value || '',
    });
    const nationalityAttribute = new Attribute({
      key: 'nationality',
      value: this.contactFormGroup.controls.contactNationality.value || '',
    });
    const vatAttribute = new Attribute({
      key: 'vat',
      value: this.businessFormGroup.controls.vat.value || '',
    });
    const taxIdNumberAttribute = new Attribute({
      key: 'tax_id_number',
      value: this.contactFormGroup.controls.taxIdNumber.value || '',
    });
    const firstNameAttribute = new Attribute({
      key: 'first_name',
      value: this.contactFormGroup.controls.contactFirstName.value || '',
    });
    const lastNameAttribute = new Attribute({
      key: 'last_name',
      value: this.contactFormGroup.controls.contactLastName.value || '',
    });
    const isSoleProprietorAttribute = new Attribute({
      key: 'is_sole_proprietor',
      value: this.isSoleProprietor() ? 'true' : 'false',
    });

    req.businessAttributes = [
      businessNameAttribute,
      birthDateAttribute,
      birthPlaceAttribute,
      nationalityAttribute,
      vatAttribute,
      taxIdNumberAttribute,
      firstNameAttribute,
      lastNameAttribute,
      isSoleProprietorAttribute,
    ];

    return req;
  }

  private addContactSupportingDocument(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const supportingDocumentInfo = new SupportingDocumentInfo();
    supportingDocumentInfo.documentType = this.contactFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_TYPE_CONTROL)?.value || '';
    supportingDocumentInfo.fileId = this.contactFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_ID_CONTROL)?.value || '';

    const contactFirstNameAttribute = new Attribute({
      key: 'first_name',
      value: this.contactFormGroup.controls.contactFirstName.value || '',
    });
    const contactLastNameAttribute = new Attribute({
      key: 'last_name',
      value: this.contactFormGroup.controls.contactLastName.value || '',
    });
    const contactDocumentNumberAttribute = new Attribute({
      key: 'document_number',
      value: this.contactFormGroup.controls.contactDocumentNumber.value || '',
    });
    const contactIssuingAuthorityAttribute = new Attribute({
      key: 'issuing_authority',
      value: this.contactFormGroup.controls.contactIssuingAuthority.value || '',
    });
    const contactIssueDateAttribute = new Attribute({
      key: 'issue_date',
      value: formatDateForBackend(this.contactFormGroup.controls.contactIssueDate.value),
    });

    supportingDocumentInfo.attributes = [
      contactFirstNameAttribute,
      contactLastNameAttribute,
      contactDocumentNumberAttribute,
      contactIssuingAuthorityAttribute,
      contactIssueDateAttribute,
    ];

    req.supportingDocuments.push(supportingDocumentInfo);

    return req;
  }

  private addBusinessAddressSupportingDocument(req: CreateRegulationInfoRequest): CreateRegulationInfoRequest {
    const supportingDocumentInfo = new SupportingDocumentInfo();
    supportingDocumentInfo.documentType =
      this.businessAddressFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_TYPE_CONTROL)?.value || '';
    supportingDocumentInfo.fileId = this.businessAddressFormGroup.get(PROOF_DOCUMENT_CONTROL)?.get(DOCUMENT_ID_CONTROL)?.value || '';

    // Special keys are used because this address needs to be processed in the backend
    supportingDocumentInfo.processAddress = true;
    const businessNameForAddressAttribute = new Attribute({
      key: BUSINESS_NAME_FOR_PROCESSING_ADDRESS,
      value: this.businessFormGroup.controls.businessName.value || '',
    });
    const streetAddressLine1Attribute = new Attribute({
      key: STREET_ADDRESS_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('streetAddress1')?.value || '',
    });
    const cityAttribute = new Attribute({
      key: CITY_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('city')?.value || '',
    });
    const stateAttribute = new Attribute({
      key: STATE_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('state')?.value || '',
    });
    const postalCodeAttribute = new Attribute({
      key: POSTAL_CODE_FOR_PROCESSING_ADDRESS,
      value: this.businessAddressFormGroup.get('address')?.get('postalCode')?.value || '',
    });
    const countryAttribute = new Attribute({
      key: COUNTRY_FOR_PROCESSING_ADDRESS,
      value: ITALY_COUNTRY_CODE,
    });
    supportingDocumentInfo.attributes = [
      businessNameForAddressAttribute,
      streetAddressLine1Attribute,
      cityAttribute,
      stateAttribute,
      postalCodeAttribute,
      countryAttribute,
    ];

    const streetAddressLine2Value = this.businessAddressFormGroup.get('address')?.get('streetAddress2')?.value || '';
    if (streetAddressLine2Value) {
      const streetAddressLine2Attribute = new Attribute({
        key: STREET_ADDRESS_SECONDARY_FOR_PROCESSING_ADDRESS,
        value: streetAddressLine2Value,
      });
      supportingDocumentInfo.attributes.push(streetAddressLine2Attribute);
    }

    req.supportingDocuments.push(supportingDocumentInfo);

    return req;
  }
}
